import { IonPage } from '@ionic/react';
import { createContext, useContext, type ReactNode } from 'react';

const AppIonPageDepthContext = createContext(false);

type AppIonPageProps = {
  children?: ReactNode;
  className?: string;
};

/**
 * Sole allowed `IonPage` wrapper for the customer app.
 *
 * Ionic StackManager applies `ion-page-invisible` (opacity:0) during animated
 * transitions. Remounting a different `IonPage` root mid-route (loading→content,
 * HOC gate→page) leaves that class stuck — blank shell while a11y still sees DOM.
 *
 * Rules:
 * - Route leaves and chrome helpers must use `AppIonPage`, never raw `IonPage`.
 * - Prefer one unconditional `<AppIonPage>{body}</AppIonPage>` per route.
 * - Nested `AppIonPage` is a no-op so HOCs can own the stable root while pages
 *   still call `AppIonPage` uniformly.
 */
export function AppIonPage({ children, className }: AppIonPageProps) {
  const nested = useContext(AppIonPageDepthContext);

  if (nested) {
    return <>{children}</>;
  }

  return (
    <AppIonPageDepthContext.Provider value={true}>
      <IonPage className={className}>{children}</IonPage>
    </AppIonPageDepthContext.Provider>
  );
}
